/**
 * CRA3D Resume Studio — Phase 5 Version history.
 *
 * List / snapshot / restore / delete named immutable versions. The
 * working copy (is_current) is never in the snapshot list — it's
 * always what the editor writes to. Snapshotting bumps the version
 * counter monotonically. Restoring an older snapshot copies its
 * sections onto the working copy WITHOUT mutating the snapshot.
 *
 * Version numbers follow the auto-increment scheme (v1, v2, ...) with
 * an optional user-supplied name + change_note. Export filenames use
 * the auto number.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  History, ArrowLeft, Plus, RotateCcw, Trash2, FileText, Lock,
} from "lucide-react";

export default function ResumeStudioVersions() {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [{ data: r }, { data: vs }] = await Promise.all([
        api.get(`/resume-studio/resumes/${resumeId}`),
        api.get(`/resume-studio/resumes/${resumeId}/versions`),
      ]);
      setResume(r.resume);
      setVersions(vs.versions || []);
    } catch (err) {
      setError(extractApiError(err, "Failed to load version history."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resumeId]);

  const doSnapshot = async () => {
    setBusy(true);
    try {
      const body = {};
      if (newName.trim()) body.name = newName.trim();
      if (newNote.trim()) body.change_note = newNote.trim();
      await api.post(`/resume-studio/resumes/${resumeId}/versions`, body);
      toast.success("Snapshot saved.");
      setNewOpen(false);
      setNewName("");
      setNewNote("");
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Snapshot failed."));
    } finally { setBusy(false); }
  };

  const doRestore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      await api.post(
        `/resume-studio/resumes/${resumeId}/versions/${restoreTarget.version_number}/restore`,
      );
      toast.success(
        `Restored from v${restoreTarget.version_number}. ` +
        "Your working copy has been updated; the snapshot is unchanged.",
      );
      setRestoreTarget(null);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Restore failed."));
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(
        `/resume-studio/resumes/${resumeId}/versions/${deleteTarget.version_number}`,
      );
      toast.success(`v${deleteTarget.version_number} deleted.`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Delete failed."));
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <AppShell>
        <p className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary"
           data-testid="resume-studio-versions-loading">
          Loading version history…
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div
        data-testid="resume-studio-versions-page"
        className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <button
          onClick={() => navigate(`/resume-studio/${resumeId}`)}
          data-testid="resume-studio-versions-back"
          className="mb-4 text-xs text-fg-secondary hover:text-fg-primary flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to the editor
        </button>

        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">
              Version history
            </p>
            <h1
              data-testid="resume-studio-versions-title"
              className="mt-1 text-2xl sm:text-3xl font-heading font-medium text-fg-primary flex items-center gap-2"
            >
              <History className="h-5 w-5" aria-hidden="true" />
              {resume?.name || "Resume"}
            </h1>
            <p className="mt-2 text-xs text-fg-secondary max-w-2xl">
              A snapshot captures the current state of every claim on every
              section. Snapshots are read-only — restoring one updates your
              working copy without touching the snapshot itself.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/resume-studio/${resumeId}/preview`}>
              <Button
                variant="ghost"
                data-testid="resume-studio-versions-open-preview-btn"
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
              >
                <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                Preview
              </Button>
            </Link>
            <Button
              onClick={() => setNewOpen(true)}
              data-testid="resume-studio-versions-new-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
              Save a snapshot
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive"
                  data-testid="resume-studio-versions-error"
                  className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {versions.length === 0 ? (
          <div
            data-testid="resume-studio-versions-empty"
            className="rounded-lg border border-border-subtle bg-bg-surface p-8 text-center"
          >
            <p className="text-sm text-fg-secondary">
              No snapshots yet. Save one before an edit you might want to
              undo, or before an export you want to re-produce later.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="resume-studio-versions-list">
            {versions.map((v) => (
              <li
                key={v.id}
                data-testid={`resume-studio-versions-row-${v.version_number}`}
                className="rounded-lg border border-border-subtle bg-bg-surface p-4 flex flex-wrap items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-fg-secondary flex items-center gap-2">
                    <span className="rounded bg-bg-surface-hover px-2 py-0.5 text-fg-primary">
                      v{v.version_number}
                    </span>
                    {v.created_at
                      && new Date(v.created_at).toLocaleString()}
                  </p>
                  <p className="mt-1 text-base font-medium text-fg-primary truncate"
                     data-testid={`resume-studio-versions-row-${v.version_number}-name`}>
                    {v.name || `Snapshot v${v.version_number}`}
                  </p>
                  {v.change_note && (
                    <p
                      data-testid={`resume-studio-versions-row-${v.version_number}-note`}
                      className="mt-1 text-xs text-fg-secondary line-clamp-2"
                    >
                      {v.change_note}
                    </p>
                  )}
                  <p className="mt-1 text-[10px] text-fg-muted">
                    id {v.id?.slice(0, 8)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRestoreTarget(v)}
                    disabled={busy}
                    data-testid={`resume-studio-versions-row-${v.version_number}-restore-btn`}
                    className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                    Restore
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteTarget(v)}
                    disabled={busy}
                    data-testid={`resume-studio-versions-row-${v.version_number}-delete-btn`}
                    className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* New snapshot dialog */}
      <Dialog open={newOpen} onOpenChange={(v) => { if (!v) setNewOpen(false); }}>
        <DialogContent
          data-testid="resume-studio-versions-new-dialog"
          className="bg-bg-surface border-border-subtle text-fg-primary"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">Save a snapshot</DialogTitle>
            <DialogDescription className="text-fg-secondary">
              Captures the current state of this resume. You'll be able
              to preview or restore it later. Snapshots are immutable —
              they don't change even if you keep editing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ver-name">Name (optional)</Label>
              <Input
                id="ver-name"
                maxLength={120}
                data-testid="resume-studio-versions-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Before I applied to Metro Library"
                className="bg-bg-base border-border-subtle text-fg-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ver-note">What did you change? (optional)</Label>
              <Textarea
                id="ver-note"
                maxLength={400}
                data-testid="resume-studio-versions-new-note"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="One line to help you remember later."
                className="bg-bg-base border-border-subtle text-fg-primary min-h-20"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => setNewOpen(false)}
              disabled={busy}
              data-testid="resume-studio-versions-new-cancel-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              Cancel
            </Button>
            <Button
              onClick={doSnapshot}
              disabled={busy}
              data-testid="resume-studio-versions-new-save-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              {busy ? "Saving…" : "Save snapshot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore confirm */}
      <Dialog open={!!restoreTarget}
               onOpenChange={(v) => { if (!v) setRestoreTarget(null); }}>
        <DialogContent
          data-testid="resume-studio-versions-restore-dialog"
          className="bg-bg-surface border-border-subtle text-fg-primary"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">
              Restore v{restoreTarget?.version_number}?
            </DialogTitle>
            <DialogDescription className="text-fg-secondary">
              This will replace your working copy's sections with the
              content from this snapshot. Any unsaved edits on the
              working copy will be overwritten. The snapshot itself
              stays as-is.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => setRestoreTarget(null)}
              disabled={busy}
              data-testid="resume-studio-versions-restore-cancel-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              Keep working copy
            </Button>
            <Button
              onClick={doRestore}
              disabled={busy}
              data-testid="resume-studio-versions-restore-confirm-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              <RotateCcw className="h-4 w-4 mr-2" aria-hidden="true" />
              {busy ? "Restoring…" : "Restore snapshot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget}
               onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent
          data-testid="resume-studio-versions-delete-dialog"
          className="bg-bg-surface border-border-subtle text-fg-primary"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">
              Delete v{deleteTarget?.version_number}?
            </DialogTitle>
            <DialogDescription className="text-fg-secondary">
              Permanent. The snapshot will be removed from the version
              list. Your working copy is not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={busy}
              data-testid="resume-studio-versions-delete-cancel-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              Keep the snapshot
            </Button>
            <Button
              variant="destructive"
              onClick={doDelete}
              disabled={busy}
              data-testid="resume-studio-versions-delete-confirm-btn"
            >
              {busy ? "Deleting…" : "Delete snapshot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
